import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, KeyRound, Save, PlugZap, MessageSquareText, Building2, Ban, Play,
  XCircle, Phone, RefreshCw, Rocket, DollarSign,
} from "lucide-react";
import { platformTwilioApi } from "../../lib/api";

const inputCls =
  "w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-[13px] text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30";

const STATUS_STYLE = {
  active: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  suspended: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  closed: "text-rose-300 bg-rose-500/10 border-rose-500/30",
};

/** Platform Console → Twilio tab: master account, billing config, subaccounts. */
export default function PlatformTwilioCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      setData(await platformTwilioApi.overview());
    } catch (e) {
      toast.error("Failed to load Twilio data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 text-cyan-400 animate-spin" /></div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="platform-twilio-panel">
      <div className="grid lg:grid-cols-2 gap-5">
        <MasterConfigCard config={data.config} onSaved={reload} />
        <BillingConfigCard config={data.config} onSaved={reload} />
      </div>
      <SubaccountsTable companies={data.companies} configReady={data.config.configured} onChange={reload} />
    </div>
  );
}

function MasterConfigCard({ config, onSaved }) {
  const [accountSid, setAccountSid] = useState(config.accountSid || "");
  const [apiKeySid, setApiKeySid] = useState(config.apiKeySid || "");
  const [apiKeySecret, setApiKeySecret] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [platformInboxNumber, setPlatformInboxNumber] = useState(config.platformInboxNumber || "");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      await platformTwilioApi.saveConfig({
        accountSid,
        apiKeySid,
        platformInboxNumber,
        ...(apiKeySecret ? { apiKeySecret } : {}),
        ...(authToken ? { authToken } : {}),
      });
      setApiKeySecret("");
      setAuthToken("");
      toast.success("Master account saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await platformTwilioApi.test();
      setTestResult(res);
      toast.success("Twilio connection verified");
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Connection failed" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="twilio-master-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-4 w-4 text-cyan-400" />
        <h3 className="text-[14px] font-semibold text-white">Master account (parent)</h3>
        <span
          data-testid="twilio-master-status"
          className={`text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${
            config.configured
              ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
              : "text-amber-300 bg-amber-500/10 border-amber-500/30"
          }`}
        >
          {config.configured ? "Configured" : "Not configured"}
        </span>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">
        All subaccounts are created under this account and bill to it. Use a <b>Main</b> API key
        (standard keys can't manage subaccounts) or your master Auth Token.
      </p>
      <div className="space-y-3">
        <Field label="Master Account SID (AC…)">
          <input value={accountSid} onChange={(e) => setAccountSid(e.target.value)} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" className={inputCls} data-testid="twilio-master-account-sid" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="API Key SID (SK…)">
            <input value={apiKeySid} onChange={(e) => setApiKeySid(e.target.value)} placeholder="SKxxxxxxxxxxxxxxxx" className={inputCls} data-testid="twilio-master-api-key-sid" />
          </Field>
          <Field label="API Key Secret">
            <input type="password" value={apiKeySecret} onChange={(e) => setApiKeySecret(e.target.value)} placeholder={config.apiKeySecretSet ? "•••••••• (saved)" : "API key secret"} className={inputCls} data-testid="twilio-master-api-key-secret" />
          </Field>
        </div>
        <Field label="Auth Token (alternative to API key)">
          <input type="password" value={authToken} onChange={(e) => setAuthToken(e.target.value)} placeholder={config.authTokenSet ? "•••••••• (saved)" : "Master auth token (optional)"} className={inputCls} data-testid="twilio-master-auth-token" />
        </Field>
        <Field label="Platform inbox number (E.164 — your own SMS number)">
          <input
            value={platformInboxNumber}
            onChange={(e) => setPlatformInboxNumber(e.target.value)}
            placeholder="+14155550123"
            className={inputCls}
            data-testid="twilio-platform-inbox-number"
          />
          <div className="mt-1 text-[10.5px] text-slate-500">
            Inbound texts to this number land in <span className="text-slate-300">Platform Console → Inbox</span> (not any tenant). Outbound platform replies also send from here.
          </div>
        </Field>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button onClick={save} disabled={saving} data-testid="twilio-master-save" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
        <button onClick={test} disabled={testing} data-testid="twilio-master-test" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px] font-medium disabled:opacity-50">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test connection
        </button>
      </div>
      {testResult && (
        <div
          data-testid="twilio-test-result"
          className={`mt-3 rounded-lg border p-3 text-[12.5px] ${
            testResult.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-rose-500/30 bg-rose-500/10 text-rose-200"
          }`}
        >
          {testResult.ok
            ? <>Connected to <b>{testResult.friendlyName}</b> ({testResult.type}, {testResult.status}) — {testResult.subaccounts} open subaccount(s).</>
            : testResult.error}
        </div>
      )}
    </div>
  );
}

function BillingConfigCard({ config, onSaved }) {
  const [smsCostUsd, setSmsCostUsd] = useState(String(config.smsCostUsd ?? 0.0079));
  const [chargeCredits, setChargeCredits] = useState(!!config.chargeCredits);
  const [blockAtZero, setBlockAtZero] = useState(!!config.blockAtZero);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await platformTwilioApi.saveConfig({
        smsCostUsd: parseFloat(smsCostUsd) || 0,
        chargeCredits,
        blockAtZero,
      });
      toast.success("SMS billing settings saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="twilio-billing-card">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className="h-4 w-4 text-emerald-400" />
        <h3 className="text-[14px] font-semibold text-white">SMS billing (markup via AI credits)</h3>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">
        Each outbound text deducts credits from the company's AI-credit wallet:
        <span className="text-slate-300"> base cost × (1 + your AI-credits profit %)</span>. Tune the profit % on the AI Credits tab.
      </p>
      <div className="space-y-3">
        <Field label="Base Twilio cost per outbound SMS segment (USD)">
          <input value={smsCostUsd} onChange={(e) => setSmsCostUsd(e.target.value)} type="number" step="0.0001" min="0" className={inputCls} data-testid="twilio-sms-cost" />
        </Field>
        <Toggle
          label="Charge companies per SMS (deduct AI credits)"
          hint="Off = you absorb all SMS costs."
          checked={chargeCredits}
          onChange={setChargeCredits}
          testid="twilio-charge-credits-toggle"
        />
        <Toggle
          label="Pause texting when a company's wallet hits 0"
          hint="Off = texts keep sending even with an empty wallet (you eat the cost)."
          checked={blockAtZero}
          onChange={setBlockAtZero}
          testid="twilio-block-zero-toggle"
        />
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={save} disabled={saving} data-testid="twilio-billing-save" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save billing
        </button>
      </div>
    </div>
  );
}

function SubaccountsTable({ companies, configReady, onChange }) {
  const [busy, setBusy] = useState(""); // "{companyId}:{action}"
  const [confirmClose, setConfirmClose] = useState(null); // company row
  const [live, setLive] = useState({}); // companyId -> {messages, twilioCostUsd} | "loading"

  const run = async (companyId, action, fn, okMsg) => {
    setBusy(`${companyId}:${action}`);
    try {
      await fn();
      toast.success(okMsg);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  const pullLive = async (companyId) => {
    setLive((s) => ({ ...s, [companyId]: "loading" }));
    try {
      const res = await platformTwilioApi.liveUsage(companyId);
      setLive((s) => ({ ...s, [companyId]: res }));
    } catch (e) {
      setLive((s) => ({ ...s, [companyId]: undefined }));
      toast.error(e?.response?.data?.detail || "Could not pull Twilio usage");
    }
  };

  const missing = companies.filter((c) => !c.provisioned).length;

  const [syncing, setSyncing] = useState(false);
  const syncBilling = async () => {
    setSyncing(true);
    try {
      const res = await platformTwilioApi.syncBilling();
      const n = (res.changes || []).length;
      if (n) {
        toast.success(`${n} subaccount(s) updated: ${res.changes.map((c) => `${c.name} → ${c.status}`).join(", ")}`);
        onChange();
      } else {
        toast.info("All subaccounts already match their billing status");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="twilio-subaccounts-table">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-cyan-400" />
          <h3 className="text-[14px] font-semibold text-white">Company subaccounts</h3>
          <span className="text-[11.5px] text-slate-500">
            {companies.filter((c) => c.provisioned).length}/{companies.length} provisioned · usage = this month · auto-syncs with billing hourly
          </span>
        </div>
        <div className="flex items-center gap-2">
          {configReady && (
            <button
              data-testid="twilio-sync-billing"
              disabled={syncing}
              onClick={syncBilling}
              title="Suspend subaccounts of past-due/canceled companies and reactivate auto-suspended ones whose billing is healthy"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px] font-medium disabled:opacity-50"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync billing
            </button>
          )}
          {configReady && missing > 0 && (
            <button
              data-testid="twilio-provision-all"
              disabled={busy === "all:provision"}
              onClick={() => run("all", "provision", () => platformTwilioApi.provisionAll(), "Provisioning complete")}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50"
            >
              {busy === "all:provision" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
              Provision {missing} missing
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left min-w-[920px]">
          <thead>
            <tr className="text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-white/10">
              <th className="px-5 py-2.5">Company</th>
              <th className="px-3 py-2.5">Subaccount</th>
              <th className="px-3 py-2.5">Number</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5 text-right">Msgs</th>
              <th className="px-3 py-2.5 text-right">Billed credits</th>
              <th className="px-3 py-2.5 text-right">Twilio cost (sms / calls)</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => {
              const lv = live[c.companyId];
              return (
                <tr key={c.companyId} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`twilio-row-${c.companyId}`}>
                  <td className="px-5 py-3">
                    <div className="text-[13px] font-medium text-slate-200">{c.name}</div>
                    <div className="text-[10.5px] text-slate-500">{c.companyId}</div>
                  </td>
                  <td className="px-3 py-3 text-[11.5px] font-mono text-slate-400">
                    {c.subSid ? `${c.subSid.slice(0, 10)}…${c.subSid.slice(-4)}` : "—"}
                  </td>
                  <td className="px-3 py-3 text-[12.5px] text-slate-300">
                    {c.number ? (
                      <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-emerald-400" />{c.number}</span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {c.provisioned ? (
                      <span className={`text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${STATUS_STYLE[c.status] || "text-slate-400 bg-white/5 border-white/10"}`}>
                        {c.status || "unknown"}{c.status === "suspended" && c.suspendedBy === "auto" ? " (billing)" : ""}
                      </span>
                    ) : (
                      <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded border text-slate-500 bg-white/5 border-white/10">not provisioned</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right text-[12.5px] text-slate-300">{c.usage.messages}</td>
                  <td className="px-3 py-3 text-right text-[12.5px] text-amber-300">{c.usage.credits}</td>
                  <td className="px-3 py-3 text-right text-[12px] text-slate-400">
                    {lv === "loading" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin inline text-cyan-400" />
                    ) : lv ? (
                      <div className="flex flex-col items-end gap-0.5" data-testid={`twilio-live-usage-result-${c.companyId}`}>
                        <span>${lv.twilioCostUsd} <span className="text-slate-600">({lv.messages} sms)</span></span>
                        <span className="text-slate-500">${lv.twilioCallsCostUsd ?? 0} <span className="text-slate-600">({lv.callMinutes ?? 0}m / {lv.calls ?? 0} calls)</span></span>
                      </div>
                    ) : c.provisioned ? (
                      <button onClick={() => pullLive(c.companyId)} data-testid={`twilio-live-usage-${c.companyId}`} className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 text-[11.5px]">
                        <RefreshCw className="h-3 w-3" /> pull
                      </button>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {!c.provisioned || c.status === "closed" ? (
                        <RowBtn
                          testid={`twilio-provision-${c.companyId}`}
                          busy={busy === `${c.companyId}:provision`}
                          disabled={!configReady}
                          title={configReady ? "Create a Twilio subaccount" : "Configure the master account first"}
                          onClick={() => run(c.companyId, "provision", () => platformTwilioApi.provision(c.companyId), `Subaccount created for ${c.name}`)}
                          icon={Rocket} label="Provision" cls="text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/10"
                        />
                      ) : (
                        <>
                          {c.status === "suspended" ? (
                            <RowBtn
                              testid={`twilio-reactivate-${c.companyId}`}
                              busy={busy === `${c.companyId}:active`}
                              onClick={() => run(c.companyId, "active", () => platformTwilioApi.setStatus(c.companyId, "active"), `${c.name} reactivated`)}
                              icon={Play} label="Reactivate" cls="text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10"
                            />
                          ) : (
                            <RowBtn
                              testid={`twilio-suspend-${c.companyId}`}
                              busy={busy === `${c.companyId}:suspended`}
                              onClick={() => run(c.companyId, "suspended", () => platformTwilioApi.setStatus(c.companyId, "suspended"), `${c.name} suspended`)}
                              icon={Ban} label="Suspend" cls="text-amber-300 border-amber-500/30 hover:bg-amber-500/10"
                            />
                          )}
                          <RowBtn
                            testid={`twilio-close-${c.companyId}`}
                            busy={busy === `${c.companyId}:closed`}
                            onClick={() => setConfirmClose(c)}
                            icon={XCircle} label="Close" cls="text-rose-300 border-rose-500/30 hover:bg-rose-500/10"
                          />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {companies.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-[13px] text-slate-500">No companies yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmClose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirmClose(null)}>
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#0F1524] p-5" onClick={(e) => e.stopPropagation()} data-testid="twilio-close-confirm-modal">
            <h4 className="text-[15px] font-semibold text-white mb-2">Permanently close this subaccount?</h4>
            <p className="text-[12.5px] text-slate-400 mb-4">
              Closing <b className="text-slate-200">{confirmClose.name}</b>'s Twilio subaccount is <b>permanent</b> — its
              phone number{confirmClose.number ? ` (${confirmClose.number})` : ""} will be released and texting stops.
              Use <b>Suspend</b> for a temporary hold instead.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmClose(null)} data-testid="twilio-close-cancel" className="px-3.5 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px]">Cancel</button>
              <button
                data-testid="twilio-close-confirm"
                onClick={() => { const row = confirmClose; setConfirmClose(null); run(row.companyId, "closed", () => platformTwilioApi.setStatus(row.companyId, "closed"), `${row.name}'s subaccount closed`); }}
                className="px-3.5 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-[13px] font-semibold"
              >
                Close permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RowBtn({ onClick, icon: Icon, label, cls, busy, disabled, title, testid }) {
  return (
    <button
      onClick={onClick} disabled={busy || disabled} title={title} data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11.5px] font-medium disabled:opacity-40 ${cls}`}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />} {label}
    </button>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-[12.5px] font-medium text-slate-200">{label}</div>
        {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
      </div>
      <button
        onClick={() => onChange(!checked)} data-testid={testid} role="switch" aria-checked={checked}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-white/10"}`}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
